/**
 * Escrow trade window — two columns, no trust required. You stage from
 * your real stock (server-validated again at the swap), the other side's
 * offer renders live, and ANY change voids both confirmations. The swap
 * itself is the server's; this window only stages and displays.
 */
import { ITEMS, type TradeOffer } from "@hyperlight/shared";

export class TradeWindow {
  private el: HTMLElement;
  private peerEl: HTMLElement;
  private mineEl: HTMLElement;
  private theirsEl: HTMLElement;
  private credInput: HTMLInputElement;
  private statusEl: HTMLElement;
  private confirmBtn: HTMLElement;
  visible = false;
  /** Local staging (server echo overwrites on each tradeState). */
  private staged = new Map<string, number>();
  private stagedCredits = 0;
  private offerTimer: number | null = null;

  constructor(
    private handlers: {
      onOffer: (offer: TradeOffer) => void;
      onConfirm: () => void;
      onCancel: () => void;
      stockOf: (id: string) => number;
      credits: () => number;
      /** Distinct item ids the player holds (staging rows). */
      held: () => string[];
    },
  ) {
    this.el = document.createElement("div");
    this.el.id = "trade-window";
    this.el.innerHTML = `
      <div class="tw-panel">
        <div class="tw-title">TRADING WITH <span class="tw-peer"></span></div>
        <div class="tw-cols">
          <div class="tw-col">
            <div class="tw-col-title">YOUR OFFER</div>
            <div class="tw-mine"></div>
            <label class="tw-credits">CREDITS <input type="number" min="0" step="1" value="0" /></label>
          </div>
          <div class="tw-col">
            <div class="tw-col-title">THEIR OFFER</div>
            <div class="tw-theirs"></div>
          </div>
        </div>
        <div class="tw-status"></div>
        <div class="tw-actions">
          <button class="tw-confirm">CONFIRM</button>
          <button class="tw-cancel">CANCEL</button>
        </div>
      </div>`;
    this.el.style.display = "none";
    document.body.appendChild(this.el);
    this.peerEl = this.el.querySelector(".tw-peer")!;
    this.mineEl = this.el.querySelector(".tw-mine")!;
    this.theirsEl = this.el.querySelector(".tw-theirs")!;
    this.credInput = this.el.querySelector(".tw-credits input")!;
    this.statusEl = this.el.querySelector(".tw-status")!;
    this.confirmBtn = this.el.querySelector(".tw-confirm")!;
    this.confirmBtn.addEventListener("click", () => this.handlers.onConfirm());
    this.el.querySelector(".tw-cancel")!.addEventListener("click", () => this.handlers.onCancel());
    this.credInput.addEventListener("input", () => {
      const v = Math.max(0, Math.min(this.handlers.credits(), Math.floor(Number(this.credInput.value) || 0)));
      this.stagedCredits = v;
      this.queueOffer();
    });
    this.mineEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest(".tw-adj") as HTMLElement | null;
      if (!btn) return;
      const id = btn.dataset.id!;
      const delta = btn.dataset.d === "+" ? 1 : -1;
      const cur = this.staged.get(id) ?? 0;
      const next = Math.max(0, Math.min(this.handlers.stockOf(id), cur + delta));
      if (next === 0) this.staged.delete(id);
      else this.staged.set(id, next);
      this.renderMine();
      this.queueOffer();
    });
  }

  private queueOffer(): void {
    if (this.offerTimer !== null) clearTimeout(this.offerTimer);
    this.offerTimer = window.setTimeout(() => {
      this.offerTimer = null;
      this.handlers.onOffer({
        items: [...this.staged].map(([id, n]) => ({ id, n })),
        credits: this.stagedCredits,
      });
    }, 280);
  }

  open(peerName: string): void {
    this.peerEl.textContent = peerName.toUpperCase();
    this.staged.clear();
    this.stagedCredits = 0;
    this.credInput.value = "0";
    this.renderMine();
    this.theirsEl.innerHTML = "";
    this.statusEl.textContent = "stage items, then confirm — both must agree";
    this.el.style.display = "flex";
    this.visible = true;
  }

  /** Authoritative session state from the server. */
  setState(
    you: TradeOffer,
    them: TradeOffer,
    youConfirmed: boolean,
    themConfirmed: boolean,
    err?: string,
  ): void {
    this.staged = new Map(you.items.map((i) => [i.id, i.n]));
    this.stagedCredits = you.credits;
    if (document.activeElement !== this.credInput) this.credInput.value = String(you.credits);
    this.renderMine();
    this.theirsEl.innerHTML =
      them.items
        .map(
          (i) =>
            `<div class="tw-row"><span>${(ITEMS[i.id]?.name ?? i.id).toUpperCase()}</span><b>× ${i.n}</b></div>`,
        )
        .join("") +
      (them.credits > 0 ? `<div class="tw-row"><span>CREDITS</span><b>${them.credits}</b></div>` : "");
    this.statusEl.textContent = err
      ? err
      : themConfirmed && youConfirmed
        ? "EXECUTING…"
        : themConfirmed
          ? "THEY CONFIRMED — your move"
          : youConfirmed
            ? "waiting for them…"
            : "stage items, then confirm — both must agree";
    this.statusEl.classList.toggle("tw-err", !!err);
    this.confirmBtn.classList.toggle("tw-armed", youConfirmed);
    this.confirmBtn.textContent = youConfirmed ? "CONFIRMED ✓" : "CONFIRM";
  }

  private renderMine(): void {
    const rows: string[] = [];
    for (const id of this.handlers.held()) {
      const n = this.staged.get(id) ?? 0;
      const stock = this.handlers.stockOf(id);
      rows.push(`
        <div class="tw-row">
          <span>${(ITEMS[id]?.name ?? id).toUpperCase()}<i>/${stock}</i></span>
          <span class="tw-stage">
            <button class="tw-adj" data-id="${id}" data-d="-" ${n === 0 ? "disabled" : ""}>−</button>
            <b>${n}</b>
            <button class="tw-adj" data-id="${id}" data-d="+" ${n >= stock ? "disabled" : ""}>+</button>
          </span>
        </div>`);
    }
    this.mineEl.innerHTML = rows.join("") || `<div class="tw-row"><span>nothing to trade</span></div>`;
  }

  close(): void {
    this.el.style.display = "none";
    this.visible = false;
    if (this.offerTimer !== null) clearTimeout(this.offerTimer);
    this.offerTimer = null;
  }
}
